<template>
	<FormCard class="">
		<h3>Per {{ labelText }}</h3>
		<div class="flex">
			<ag-grid-vue
				:theme="resultGridTheme"
				style="width: 100%; min-height: 350px"
				:rowData="filteredResults"
				:columnDefs="agGridColumnDefs"
				:loadingOverlayComponentParams="{ loadingMessage: 'Loading result by step. Please wait.' }"
				:noRowsOverlayComponentParams="{ noRowsMessageFunc: () => 'No result by step found.' }"
				:overlayLoadingTemplate="'<span class=&quot;ag-overlay-loading-center&quot;>Loading result by step. Please wait.</span>'"
				:overlayNoRowsTemplate="'<span class=&quot;ag-overlay-no-rows-center&quot;>No result by step found.</span>'"
				:context="{}"
				:loadThemeGoogleFonts="true"
				:defaultColDef="{ wrapHeaderText: true, autoHeaderHeight: true }"
				:pinnedBottomRowData="pinnedBottomRow"
				@grid-ready="onGridReady"
			>
			</ag-grid-vue>
		</div>
	</FormCard>
</template>
<script lang="ts" setup>
import { ref, onMounted, computed } from "vue"
import { FinalResult, EmissionFactors } from "../../entities/NSwagGenerated"
import { AgGridVue } from "ag-grid-vue3"
import {
	getConditionalFormatColor,
	valueToStringFormatter,
	resultGridTheme,
	BuildMinMaxValuesFromEmissionFactors,
} from "../composables/resultGridHelper"
import { GridApi, ColDef, CellStyle, GridReadyEvent, CellStyleFunc } from "ag-grid-community"

type ResultRow = {
	stageName?: string
	stepName?: string
	materialName?: string
	emissionFactors?: Record<string, number>
	totalWeightedScore?: number
}

const props = defineProps({
	results: {
		type: Array as () => ResultRow[],
		required: false,
		default: () => [],
	},
	activeEmissionFactor: {
		type: Object as () => EmissionFactors,
		required: false,
		default: () => ({}) as EmissionFactors,
	},
	labelText: {
		type: String,
		required: false,
		default: "",
	},
	showPercentOfTotal: {
		type: Boolean,
		required: false,
		default: false,
	},
	labelColumnField: {
		type: String,
		required: false,
		default: "",
	},
	labelColumnHeaderName: {
		type: String,
		required: false,
		default: "",
	},
	grandTotalLabel: {
		type: String,
		required: false,
		default: "Grand Total",
	},
})

// Grid API reference
const gridApi = ref<GridApi | null>(null)

function onGridReady(params: GridReadyEvent) {
	gridApi.value = params.api
}

// Filter out "Total" rows
const filteredResults = computed(
	() =>
		props.results?.filter((row) => {
			const label = row?.[props.labelColumnField as keyof ResultRow]
			return !label || label.toString().trim().toLowerCase() !== "total"
		}) ?? []
)

// Pre-calculate min/max values for conditional formatting
const columnMinMax = computed(() => {
	if (!filteredResults.value?.length) return []
	const emissionFactors = filteredResults.value.map((row) => row.emissionFactors ?? {})
	return BuildMinMaxValuesFromEmissionFactors(emissionFactors)
})

// Pinned bottom row totals
const pinnedBottomRow = computed(() => {
	if (!filteredResults.value?.length) return []

	const efName = props.activeEmissionFactor.emissionFactorName
	const totals: Record<string, any> = {
		[props.labelColumnField]: props.grandTotalLabel,
		totalWeightedScore: 0,
		emissionFactors: {} as { [key: string]: number },
	}

	for (const row of filteredResults.value) {
		totals.totalWeightedScore += row.totalWeightedScore || 0
		if (efName) {
			totals.emissionFactors[efName] = (totals.emissionFactors[efName] || 0) + (row.emissionFactors?.[efName] ?? 0)
		}
	}

	return [totals]
})

// AG Grid column definitions
const agGridColumnDefs = computed(() => {
	const primaryEF = props.activeEmissionFactor
	if (!primaryEF?.emissionFactorName) return []

	const efName = primaryEF.emissionFactorName

	// Pre-calculate total for percent column
	const totalEF = filteredResults.value.reduce((sum, row) => sum + (row.emissionFactors?.[efName] ?? 0), 0)

	const labelColumn: ColDef = {
		field: props.labelColumnField,
		headerName: props.labelColumnHeaderName,
		flex: 1,
		minWidth: 100,
	}

	const efColumn: ColDef = {
		field: `emissionFactors.${efName}`,
		headerName: efName,
		flex: 1,
		minWidth: 100,
		valueGetter: (p: any) => p.data?.emissionFactors?.[efName] ?? 0,
		valueFormatter: (p: any) => { if (p.value === 0) return "-"
							return valueToStringFormatter(p.value)
							},
		cellStyle: { textAlign: "right" },
		aggFunc: "sum",
	}

	const percentColumn: ColDef = {
		headerName: "Percent of Total",
		flex: 1,
		minWidth: 100,
		valueGetter: (params: any) => {
			const efValue = params.data?.emissionFactors?.[efName] ?? 0
			return totalEF > 0 ? (efValue / totalEF) * 100 : 0
		},
		valueFormatter: (p: any) => (p.value === 0 ? "-" : `${p.value.toFixed(1)}%`),
		cellStyle: (params: any): CellStyle | null => {
			if (params.node.rowPinned) return { textAlign: "right", backgroundColor: "transparent" }

			const efValue = params.data?.emissionFactors?.[efName] ?? 0
			if (efValue === 0) return { textAlign: "right", backgroundColor: "transparent" }

			const minMax = columnMinMax.value.find((item) => item.columnName === efName)
			if (minMax) {
				return { textAlign: "right", backgroundColor: getConditionalFormatColor(efValue, minMax.min, minMax.max) }
			}

			return { textAlign: "right", backgroundColor: "" }
		},
		aggFunc: "sum",
	}

	// Include percent column only if requested
	return [labelColumn, efColumn, ...(props.showPercentOfTotal ? [percentColumn] : [])]
})

// Expose methods for parent
defineExpose({
	getResults: () => filteredResults.value,
	getGridApi: () => gridApi.value,
})

onMounted(() => {
	// Component mounted
})
</script>
